import {
  BlotterTable,
  BlotterTableFilters,
  Button,
  createCSVFileName,
  Divider,
  FormattedMessage,
  FormControlSizes,
  MixpanelEvent,
  Toggle,
  useMixpanel,
} from '@talos/kyoko';
import { useCallback, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { CustomHyperlinkButtons } from '../CustomHyperlinkButtons';
import { BlotterActions } from '../styles';
import type { TabbedBlotterProps } from '../types';
import { useMarginBlotter } from './hooks/useMarginBlotter';

const messages = defineMessages({
  exportMargin: {
    defaultMessage: 'Export Margin',
    id: 'Blotters.MarginBlotter.exportMargin',
  },
  showZeroBalances: {
    defaultMessage: 'Show Zero Balances',
    id: 'Blotters.MarginBlotter.showZeroBalances',
  },
});

export type MarginBlotterProps = Required<Pick<TabbedBlotterProps, 'renderInBackground'>>;

export const MarginBlotter = ({ renderInBackground: hidden }: MarginBlotterProps) => {
  const mixpanel = useMixpanel();
  const { formatMessage } = useIntl();
  const [showZeroBalances, setShowZeroBalances] = useState(true);

  const handleShowZeroBalances = useCallback(
    (checked: boolean) => {
      setShowZeroBalances(checked);
      mixpanel.track(MixpanelEvent.ToggleShowZeroBalances, { checked });
    },
    [mixpanel]
  );

  const { blotterTable, filterBuilderAccordion } = useMarginBlotter({ showZeroBalances });

  const handleExportMargin = useCallback(() => {
    blotterTable.exportDataAsCSV({
      includeHiddenColumns: true,
      fileName: createCSVFileName({
        name: 'Margin',
      }),
    });
    mixpanel.track(MixpanelEvent.ExportMargin);
  }, [blotterTable, mixpanel]);

  return (
    <>
      {!hidden && (
        <BlotterTableFilters
          {...filterBuilderAccordion}
          {...blotterTable.blotterTableFiltersProps}
          showFilterBuilder={false}
          prefix={
            <BlotterActions>
              <CustomHyperlinkButtons blotter="margin" />
              <Button size={FormControlSizes.Small} onClick={handleExportMargin}>
                <FormattedMessage {...messages.exportMargin} />
              </Button>
              <Divider orientation="vertical" my="spacing4" />
              <Toggle
                onChange={handleShowZeroBalances}
                checked={showZeroBalances}
                label={formatMessage(messages.showZeroBalances)}
                size={FormControlSizes.Small}
              />
            </BlotterActions>
          }
        />
      )}
      <BlotterTable {...blotterTable} />
    </>
  );
};
